//シート削除画面
import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import { deleteSheet } from '../redux/sheetDbSlice';
import { deleteSheetInformation } from '../redux/sheetInformationDbSlice';
import { deleteCard } from '../redux/cardDbSlice';
import { deleteCardAttribute } from '../redux/cardAttributeDbSlice';
import { deleteCardShape } from '../redux/cardShapeDbSlice';
import { deleteMemoCardContent } from '../redux/memoCardContentDbSlice';
import { deleteUiCardItem } from '../redux/uiCardItemDbSlice';
import { deleteDrawCardContent } from '../redux/drawCardContentDbSlice';
import { deleteArrow } from '../redux/arrowDbSlice';
import { deleteSheetTree, updateSheetTree, SheetTreeDb } from '../redux/sheetTreeDbSlice';
import { setDefaultSheet } from '../redux/sheetDbSlice';
import { UserCondition, updateUserCondition } from '../redux/userConditionSlice';
import { deleteNote } from '../redux/noteDbSlice';
import useCurrentDb from '../hooks/useCurrentDb';
import useSheetsDb from '../hooks/useSheetsDb';
import useCurrentTreeHash from '../hooks/useCurrentTreeHash';
import useSheetInformationDbs from '../hooks/useSheetInformationDbs';
import useAllCardDbsInSheet from '../hooks/useAllCardDbsInSheet';
import useCardAttributeIdsByCards from '../hooks/useCardAttributeIdsByCards';
import useCardShapeIdsByCards from '../hooks/useCardShapeIdsByCards';
import useMemoCardContentIdsByCards from '../hooks/useMemoCardContentIdsByCards';
import useUiCardItemIdsByCards from '../hooks/useUiCardItemIdsByCards';
import useDrawCardContentIdsByCards from '../hooks/useDrawCardContentIdsByCards';
import useArrowDbsInSheet from '../hooks/useArrowDbsInSheet';
import useSheetTreeDbBySheet from '../hooks/useSheetTreeDbBySheet';
import useChildrenTreeIds from '../hooks/useChildrenTreeIds';
import useSheetTreeDbsByBook from '../hooks/useSheetTreeDbsByBook';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useUserCondition from '../hooks/useUserCondition';
import useNoteDbsByReferences from '../hooks/useNoteDbsByReferences';

import { deepCopy } from '../general';
import { WorkAreaStateType } from './BookEditor';
import { FoldedHash } from './BookTree';

type DeleteSheetDialogProps = {
  open: boolean;
  onClose: () => void;
  nodeClicked: (workArea: WorkAreaStateType, id?: string) => void;
  folded: FoldedHash;
}
const DeleteSheetDialog: React.FC<DeleteSheetDialogProps> = ({open, onClose, nodeClicked, folded}) => {
  const dispatch = useDispatch();
  const userId = useCurrentUserId();
  const [userConditionId, userCondition] = useUserCondition(userId);
  const [sheetId, bookId] = useCurrentDb();
  const [sheetIds] = useSheetsDb(bookId);
  const sheetListValue = useCurrentTreeHash(bookId, folded);
  const [sheetInformationIds] = useSheetInformationDbs();
  const [cardIds] = useAllCardDbsInSheet(sheetId);
  const cardAttributeIds = useCardAttributeIdsByCards(cardIds);
  const cardShapeIds = useCardShapeIdsByCards(cardIds);
  const memoContentIds = useMemoCardContentIdsByCards(cardIds);
  const uiItemIds = useUiCardItemIdsByCards(cardIds);
  const drawContentIds = useDrawCardContentIdsByCards(cardIds);
  const [arrowIds] = useArrowDbsInSheet(sheetId);
  const [sheetTreeId, sheetTreeValue] = useSheetTreeDbBySheet(sheetId);
  const childrenIds = useChildrenTreeIds(sheetId || undefined);
  const [sheetTreeIds, sheetTreeValues] = useSheetTreeDbsByBook(bookId || undefined);
  const [noteIds] = useNoteDbsByReferences([sheetId, ...cardIds, ...childrenIds.sheetIds, ...childrenIds.cardIds]);

  if (!sheetId) {
    if (open) alert('シートが見つかりません');
    return <></>;
  }

  const changeFocus = () => {
    let nextSheetId = '';
    const target = sheetListValue[sheetId];
    const order = sheetTreeValue.order;
    const appearanceOrder = target.appearanceOrder;
    const parentId = sheetTreeValue.parentSheetId;
    const targetTrees: SheetTreeDb[] = [];
    const targetIds: string[] = [];
    const updateTrees: SheetTreeDb[] = [];
    const updateIds: string[] = [];

    if (parentId) { /* 子ツリーのシートの場合 */
      sheetTreeValues.forEach((value, index) => {
        if (value.parentSheetId === parentId) {
          targetTrees.push(value);
          targetIds.push(sheetTreeIds[index]);
        }
      });
    } else { /* Book直下のシートの場合 */
      sheetTreeValues.forEach((value, index) => {
        if (!value.parentSheetId || value.parentSheetId === '') {
          targetTrees.push(value);
          targetIds.push(sheetTreeIds[index]);
        }
      });
    }

    if (sheetIds.length >= 2) {
      if (appearanceOrder === 0) {
        const nextTargetId = sheetIds.find(id => sheetListValue[id].deepness === target.deepness && sheetListValue[id].order === 1);
        nextSheetId = nextTargetId ? nextTargetId : '';
      } else {
        const nextTargetId = sheetIds.find(id => sheetListValue[id].appearanceOrder === appearanceOrder - 1);
        nextSheetId = nextTargetId ? nextTargetId : '';
      }
    }

    if (targetTrees.length - 1 > order) { //消されたシートのorderより大きいorderのシートがあれば、sheetTreeDbのorderを書き換える
      targetTrees.forEach((value, index) => {
        if (value.order > order) {
          updateTrees.push(value);
          updateIds.push(targetIds[index]);
        }
      });
      updateTrees.forEach((value, index) =>
        dispatch(updateSheetTree({ id: updateIds[index], order: order + index, parentSheetId: value.parentSheetId }))
      );
    }

    if (nextSheetId !== '') {
      dispatch(setDefaultSheet(nextSheetId));
      nodeClicked('sheet', nextSheetId);
    } else {
      nodeClicked('bookCover'); //Book直下の最後の1つのシートが消された場合はブックカバーにフォーカスする
    }
  }

  const updateHashData = (condition: UserCondition, ids: string[]): UserCondition => {
    const newCondition = deepCopy(condition);
    ids.forEach(id => {
      const zoomRate = newCondition.zoomRates[id];
      const noteDisplay = newCondition.noteDisplay.bookEditor[id];
      const workNoteRatio = newCondition.workNoteRatios.bookEditor[id];
      if (zoomRate) delete newCondition.zoomRates[id];
      if (typeof noteDisplay === 'boolean') delete newCondition.noteDisplay.bookEditor[id];
      if (workNoteRatio) delete newCondition.workNoteRatios.bookEditor[id];
    })
    return newCondition;
  }

  const deleteExe = (): void => {
    dispatch(deleteSheet({id: sheetId}));// シートを削除する
    sheetInformationIds.forEach(id => dispatch(deleteSheetInformation({id: id})));
    cardIds.forEach(id => dispatch(deleteCard({id: id})));// シート中のカードを削除する
    cardAttributeIds?.forEach(id => dispatch(deleteCardAttribute({id: id})));
    cardShapeIds?.forEach(id => dispatch(deleteCardShape({id: id})));
    memoContentIds?.forEach(id => dispatch(deleteMemoCardContent({id: id})));
    uiItemIds?.forEach(id => dispatch(deleteUiCardItem({id: id})));
    drawContentIds?.forEach(id => dispatch(deleteDrawCardContent({id: id})));
    arrowIds.forEach(id => dispatch(deleteArrow({id: id})));
    dispatch(deleteSheetTree({id: sheetTreeId}));
    // delete children
    childrenIds.sheetIds.forEach(id => dispatch(deleteSheet({id: id})));
    childrenIds.sheetInformationIds.forEach(id => dispatch(deleteSheetInformation({id: id})));
    childrenIds.cardIds.forEach(id => dispatch(deleteCard({id: id})));
    childrenIds.cardAttributeIds.forEach(id => dispatch(deleteCardAttribute({id: id})));
    childrenIds.cardShapeIds.forEach(id => dispatch(deleteCardShape({id: id})));
    childrenIds.memoContentIds.forEach(id => dispatch(deleteMemoCardContent({id: id})));
    childrenIds.uiCardItemIds.forEach(id => dispatch(deleteUiCardItem({id: id})));
    childrenIds.drawContentIds.forEach(id => dispatch(deleteDrawCardContent({id: id})));
    childrenIds.arrowIds.forEach(id => dispatch(deleteArrow({id: id})));
    childrenIds.sheetTreeIds.forEach(id => dispatch(deleteSheetTree({id: id})));
    // delete notes
    noteIds.forEach(id => dispatch(deleteNote({ id })))

    if (userConditionId && userCondition) {
      const newCondition = updateHashData(userCondition, [sheetId, ...childrenIds.sheetIds]);
      dispatch(updateUserCondition({
        id: userConditionId,
        zoomRates: newCondition.zoomRates,
        noteDisplay: newCondition.noteDisplay,
        workNoteRatios: newCondition.workNoteRatios
      }));
    }

    changeFocus();
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose}  maxWidth="xs" fullWidth>
      <DialogTitle>このシートを削除します</DialogTitle>
      <DialogContent />
      <DialogActions>
        <Button variant='text' color='primary' onClick={onClose}>
          キャンセル
        </Button>
        <Button variant='text' color='primary' onClick={() => deleteExe()}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteSheetDialog;
